import { type Form, FormType, type UpdateFormRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateFormMutation } from '../../../graphql/hooks/useUpdateFormMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { FormForm } from './FormForm';

type FormValues = UpdateFormRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string(),
  type: z.enum(FormType),
  url: z.string().nullish(),
  externalId: z.string().nullish(),
  labelMappings: z.object({
    create: z.array(z.string()),
    update: z.array(z.string()),
  }),
});

interface Props {
  form: Form;
}

export const FormUpdateModal = createModal<Props>((props) => {
  const { form, modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const initialValues: FormValues = {
    name: form.name,
    type: form.type,
    url: form.url,
    externalId: form.externalId,
    labelMappings: {
      create: form.labelMappings.create.map((label) => label.id),
      update: form.labelMappings.update.map((label) => label.id),
    },
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });
  const type = methods.watch('type') as FormType;

  const mutation = useUpdateFormMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          formId: form.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Form updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating form: ${err.message}`);
        });
    },
    [mutation, organization.id, form.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a form'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <FormForm type={type} />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
